import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
} from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import CoCreationSection from "../../components/Common/CoCreationSection";
import FeatureCard from "../../components/Common/FeatureCard";
import MobileCoCreationSection from "../../components/Common/MobileCoCreationSection";
import ExploreSection from "../../components/ProductDetails/ExploreSection";

const CustomizedMobileAppDevelopmentPage = () => {
  // AI Features Data for CoCreationSection
  const aiFeatures = [
    {
      id: 1,
      title: "Conversational bots for helpdesk and onboarding",
      icon: "/svg/BOT.svg",
    },
    {
      id: 2,
      title: "Smart recommendations based on user behavior",
      icon: "/svg/Smart_Recommendations.svg",
    },
    {
      id: 3,
      title: "Voice search & NLP for accessibility",
      icon: "/svg/Feedback.svg",
    },
    {
      id: 4,
      title: "Image recognition for field verification",
      icon: "/svg/Image_Recommendations.svg",
    },
    {
      id: 5,
      title: "Predictive analytics for service delivery and planning",
      icon: "/svg/Predictive.svg",
    },
  ];

  // Mobile App Key Features Data
  const mobileAppFeatures = [
    {
      id: 1,
      title: "Cross-Platform Support (Android, iOS, Web PWA)",
    },
    {
      id: 2,
      title: "Secure Login with OAuth2 / 2FA",
    },
    {
      id: 3,
      title: "Custom Dashboards and Data Sync",
    },
    {
      id: 4,
      title: "Integrated with APIs, Payment Gateways, SMS & GIS",
    },
    {
      id: 5,
      title: "Real-Time Reporting & Alerts",
    },
    {
      id: 6,
      title: "Auto Updates and Background Sync",
    },
    {
      id: 7,
      title: "Built-in Learning & Feedback Modules",
    },
  ];

  // Mobile Co-Creation Processes Data
  const mobileCoCreationProcesses = [
    {
      id: 1,
      title: "Requirement mapping & rapid wireframing",
      icon: "/svg/Requirement_Mapping.svg",
    },
    {
      id: 2,
      title: "Stakeholder consultations and field testing",
      icon: "/svg/Stakeholder2.svg",
    },
    {
      id: 3,
      title: "UX research and accessibility review",
      icon: "/svg/UX.svg",
    },
    {
      id: 4,
      title: "Prototyping and iterative development",
      icon: "/svg/Prototyping.svg",
    },
    {
      id: 5,
      title: "User training and change management",
      icon: "/svg/Training.svg",
    },
  ];

  // What We Build Categories Data
  const whatWeBuildCategories = [
    {
      id: 1,
      title: "eGovernment & Public Services",
      description: "License renewals, complaints, status tracking, digital payments",
    },
    {
      id: 2,
      title: "Citizen Engagement",
      description: "Feedback collection, polls, community alerts, public directories",
    },
    {
      id: 3,
      title: "Enterprise Mobility",
      description: "Field data collection, remote task tracking, approval workflows",
    },
    {
      id: 4,
      title: "Productivity & HR",
      description: "Leave management, self-service tools, attendance tracking",
    },
    {
      id: 5,
      title: "Data-Driven Platforms",
      description: "Dashboard visualizations, survey tools, location-based reporting",
    },
  ];

  // AI Features Points Data
  const aiFeaturePoints = [
    "Conversational Bots for helpdesk and onboarding",
    "Smart Recommendations based on user behavior",
    "Voice Search & NLP for accessibility",
    "Image Recognition for field verification",
    "Predictive Analytics for service delivery and planning",
  ];
  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Customized Mobile App Development | AI-Powered Government & Enterprise Apps in Bangladesh</title>
        <meta 
          name="description" 
          content="Build AI-enabled, secure, and inclusive mobile apps with Business Automation Ltd.—Bangladesh's trusted partner for custom development across government, enterprise, and development sectors. Future-ready, co-created, and scalable." 
        />
        <meta 
          name="keywords" 
          content="Mobile app development Bangladesh, custom mobile apps Bangladesh, government mobile apps, enterprise mobile development, AI-powered mobile apps, secure mobile development, cross-platform apps Bangladesh, native app development, mobile app security Bangladesh, digital government apps" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/customized-mobile-app-development" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/customized-mobile-app-development" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Customized Mobile App Development | AI-Powered Government & Enterprise Apps in Bangladesh" />
        <meta property="og:description" content="Build AI-enabled, secure, and inclusive mobile apps with Business Automation Ltd.—Bangladesh's trusted partner for custom development across government, enterprise, and development sectors. Future-ready, co-created, and scalable." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/customized-mobile-app-development" />
        <meta name="twitter:title" content="Customized Mobile App Development | AI-Powered Government & Enterprise Apps in Bangladesh" />
        <meta name="twitter:description" content="Build AI-enabled, secure, and inclusive mobile apps with Business Automation Ltd.—Bangladesh's trusted partner for custom development across government, enterprise, and development sectors. Future-ready, co-created, and scalable." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="Customized Mobile App Development"
        subtitle="Native & Cross-Platform Mobile Applications"
        backgroundImage="/png/bg_mad.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "Customized Mobile App Development", href: "#", isCurrentPage: true },
        ]}
      />

      <Box
        py={{ base: "30px", md: "40px" }}
        backgroundImage={"/png/background.png"}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        minH="100vh"
      >
        <Container maxW="7xl">
          <VStack spacing={8} align="start" w="full">
            {/* Header Section */}
            <VStack spacing={4} align="start" w="full">
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.5"
                textAlign="justify"
              >
                At Business Automation Limited, we specialize in building custom
                mobile apps that go beyond screens—they solve real problems,
                deliver meaningful experiences, and empower users. From
                government service apps and citizen reporting platforms to
                enterprise workflows and donor-funded digital tools, we design
                and develop apps that connect people to services—anytime,
                anywhere.
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.5"
                textAlign="justify"
              >
                With over two decades of delivering digital transformation in
                Bangladesh, our mobile app development services are backed by
                deep domain knowledge, a co-creation mindset, and cutting-edge
                technologies—including AI, ML, RPA, and cloud-native
                architectures.
              </Text>
            </VStack>

            {/* What We Build Section */}
            <Box w="full" py={8}>
              <VStack spacing={8} align="start" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  color="gray.800"
                >
                  What We Build
                </Heading>

                <Grid
                  templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                  gap={8}
                  w="full"
                >
                  {/* Left Column - What We Build Categories */}
                  <Box position="relative">
                    <HStack spacing={0} align="stretch">
                      {/* Blue Sidebar */}
                      <Box
                        bg="#02063A"
                        w="50px"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        borderLeftRadius="lg"
                        py={8}
                      >
                        {/* <Icon as={FaMobile} color="white" boxSize={8} mb={4} /> */}
                        <Text
                          color="white"
                          fontSize="sm"
                          fontWeight="bold"
                          transform="rotate(-90deg)"
                          whiteSpace="nowrap"
                          textAlign="center"
                        >
                          We develop mobile apps for
                        </Text>
                      </Box>

                      {/* Cards Container */}
                      <Box
                        bg="white"
                        p={6}
                        borderTopRightRadius="lg"
                        borderBottomRightRadius="lg"
                        flex={1}
                      >
                        <VStack align="start" spacing={3}>
                          {whatWeBuildCategories.map((category) => (
                            <HStack key={category.id} align="center" spacing={2}>
                              <Box
                                w="8px"
                                h="8px"
                                bg="#000B8C"
                                borderRadius="full"
                                mt={1.5}
                              />
                              <VStack align="start" spacing={0}>
                                <Text
                                  fontSize="md"
                                  fontWeight="bold"
                                  color="#000B8C"
                                >
                                  {category.title}
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                  {category.description}
                                </Text>
                              </VStack>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </HStack>
                  </Box>

                  {/* Right Column - AI Features */}
                  <Box position="relative">
                    <HStack spacing={0} align="stretch">
                      {/* Blue Sidebar */}
                      <Box
                        bg="#02063A"
                        w="50px"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        borderLeftRadius="lg"
                        py={8}
                      >
                        {/* <Icon as={FaMobile} color="white" boxSize={8} mb={4} /> */}
                        <Text
                          color="white"
                          fontSize="sm"
                          fontWeight="bold"
                          transform="rotate(-90deg)"
                          whiteSpace="nowrap"
                          textAlign="center"
                        >
                          AI at the Core of Every App
                        </Text>
                      </Box>

                      {/* AI Features Card */}
                      <Box
                        bg="white"
                        p={6}
                        borderTopRightRadius="lg"
                        borderBottomRightRadius="lg"
                        flex={1}
                      >
                        <VStack align="start" spacing={4}>
                          <Text fontSize="md" color="gray.600">
                            Our mobile apps are developed with AI-native
                            infrastructure, allowing us to integrate:
                          </Text>

                          <VStack align="start" spacing={6} w="full">
                            {aiFeaturePoints.map((feature, index) => (
                              <HStack key={index} align="start" spacing={3}>
                                <Box
                                  w="8px"
                                  h="8px"
                                  bg="#000B8C"
                                  borderRadius="full"
                                  mt={1.5}
                                />
                                <Text fontSize="sm" color="gray.700">
                                  {feature}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        </VStack>
                      </Box>
                    </HStack>
                  </Box>
                </Grid>
              </VStack>
            </Box>
          </VStack>
        </Container>
        <CoCreationSection
          title="AI at the Core of Every App"
          subtitle="Our mobile apps are developed with AI-native infrastructure, allowing us to integrate"
          processes={aiFeatures}
          blackTitle={"of Every App"}
          blueTitle={"AI at the Core"}
          illustrationImage="/svg/App_bot.svg"
          footerText="This future-proof approach ensures our apps improve continuously through machine learning and real-time feedback loops."
        />
        <FeatureCard
          title="Key Features of Our Custom App Development"
          features={mobileAppFeatures}
          backgroundImage="/png/share-your-idea.png"
        />
        <MobileCoCreationSection
          title="Co-Creation: How We Work With You"
          blueTitle={"Co-Creation:"}
          blackTitle={"How We Work With You"}
          subtitle="We engage stakeholders—from government units to donor program managers—to co-create mobile solutions that are relevant, usable, and scalable. Our process includes:"
          processes={mobileCoCreationProcesses}
          illustrationImage="/svg/CO-CREATION_2.svg"
          footerText="This collaborative process ensures adoption and long-term success."
        />
        <ExploreSection />
      </Box>
    </>
  );
};

export default CustomizedMobileAppDevelopmentPage;
